import { useEffect, useRef, useCallback, useState } from 'react';

const useInactivityLogout = (onLogout, timeout = 30 * 60 * 1000, warningTime = 2 * 60 * 1000) => { // 30 min timeout, 2 min warning
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const isActiveRef = useRef(true);
  const [showWarning, setShowWarning] = useState(false);
  const [warningTimeRemaining, setWarningTimeRemaining] = useState(0);

  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Hide warning if showing
    setShowWarning(false);

    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        console.log('Showing inactivity warning...');
        setShowWarning(true);
        setWarningTimeRemaining(warningTime);
      }
    }, timeout - warningTime);

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        console.log('User inactive for', timeout / 1000 / 60, 'minutes. Logging out...');
        onLogout();
      }
    }, timeout);
  }, [onLogout, timeout, warningTime]);

  const extendSession = useCallback(() => {
    console.log('Session extended by user');
    setShowWarning(false);
    resetTimeout();
  }, [resetTimeout]);

  const handleActivity = useCallback(() => {
    isActiveRef.current = true;
    if (showWarning) {
      setShowWarning(false);
    }
    resetTimeout();
  }, [resetTimeout, showWarning]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // User switched to another tab/window
      isActiveRef.current = false;
    } else {
      // User came back to the tab
      isActiveRef.current = true;
      if (showWarning) {
        setShowWarning(false);
      }
      resetTimeout();
    }
  }, [resetTimeout, showWarning]);

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the timeout
    resetTimeout();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleActivity, handleVisibilityChange, resetTimeout]);

  // Return a function to manually disable the timeout (e.g., when user logs out)
  const disableTimeout = useCallback(() => {
    isActiveRef.current = false;
    setShowWarning(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
  }, []);

  return { 
    disableTimeout, 
    showWarning, 
    warningTimeRemaining,
    extendSession 
  };
};

export default useInactivityLogout;
